<template>
  <div class="config-details f-h">
    <div v-if="company" class="f-h">
      <div class="config-details__infos">
        <EditableLabel
          v-model="company.name"
          :legend="$t('message.COMPANY_NAME') + $t('message.TWO_DOTS')"
          disabled
          generic-border
        />
        <EditableLabel
          v-model="company.id"
          :legend="$t('message.COMPANY_ID') + $t('message.TWO_DOTS')"
          disabled
          generic-border
        />
        <EditableLabel
          v-model="company.email"
          :legend="$t('message.CONTACT_EMAIL') + $t('message.TWO_DOTS')"
          disabled
          generic-border
        />
        <EditableLabel
          v-model="stateUsers.length"
          :legend="$t('message.USERS_NB') + $t('message.TWO_DOTS')"
          disabled
          generic-border
        />
        <EditableLabel
          v-model="stateTeams.length"
          :legend="$t('message.TEAMS_NB') + $t('message.TWO_DOTS')"
          disabled
          generic-border
        />
        <button class="add-button" @click="editingPassword = !editingPassword">
          <img src="/images/edit.png" alt="">
          {{ $t("message.CHANGE_CONFIG_PASSWORD") }}
        </button>

        <TransitionExpand>
          <PwdEditForm v-if="editingPassword" focus-on-mount @submit="updatePwd" />
        </TransitionExpand>
      </div>

      <!-- Other account settings -->
      <div v-if="company && company.id" class="config-details__settings">
        <h2 class="basic-sub-title">
          {{ $t('message.SETTINGS') }}
        </h2>

        <!-- 1st item : Basic settings -->
        <ExpandableSettingsItem :title="$t('message.SETTINGS_MAIN')" use-transition-expand>
          <div>
            <EditableLabel
              v-model="company.logo"
              :legend="$t('message.LOGO_URL') + $t('message.TWO_DOTS')"
              :placeholder="$t('message.LOGO_URL_PLACEHOLDER')"
              class="clickable-hoverable"
              @input="updateLogo"
            />
            <IntCheckbox
              v-model="company.logoWhiteBg"
              :title="$t('message.USE_WHITE_BG')"
              @input="updateLogo"
            />
            <h2 class="basic-sub-title">
              {{ $t('message.INFORMATIONS') }}
            </h2>
            <IntCheckbox
              v-model="company.allowSendingInfoAnyTeam"
              :title="$t('message.CONFIG_ALLOW_SENDING_INFO_ANY_TEAM')"
              @input="setParams"
            />
            <h2 class="basic-sub-title">
              {{ $t('message.ACTIONS') }}
            </h2>
            <IntCheckbox
              v-model="company.forceActionPostponementReason"
              :title="$t('message.CONFIG_FORCE_ACTION_POSTPMT_REASON')"
              @input="setParams"
            />
            <div class="field-hint">
              {{ $t('message.CONFIG_FORCE_ACTION_POSTPMT_REASON_HINT') }}
            </div>
            <h2 class="basic-sub-title">
              {{ $t('message.GW') }}
            </h2>
            <IntCheckbox
              v-model="company.disallowGWCompletedEditing"
              :title="$t('message.CONFIG_GW_DISALLOW_COMPLETED_EDITING')"
              @input="setParams"
            />

            <h2 class="basic-sub-title">
              {{ $t('message.SETTINGS') }}
            </h2>
            <IntCheckbox
              v-model="company.restrictSettingAccessUser"
              :title="$t('message.CONFIG_RESTRICT_SETTING_ACCESS_USERS')"
              @input="setParams"
            />
            <div class="field-hint">
              {{ $t('message.CONFIG_RESTRICT_SETTING_ACCESS_USERS_HINT') }}
            </div>

            <h2 class="basic-sub-title">
              {{ $t('message.PROBLEMS') }}
            </h2>
            <IntCheckbox
              v-model="company.restrictProblemCreationUpdate"
              :title="$t('message.CONFIG_RESTRICT_PROBLEM_CREATION_UPDATE')"
              @input="setParams"
            />
            <div class="field-hint">
              {{ $t('message.CONFIG_RESTRICT_PROBLEM_CREATION_UPDATE_HINT') }}
            </div>
          </div>
        </ExpandableSettingsItem>

        <!-- 2nd item : SSO SAML -->
        <ExpandableSettingsItem
          :title="$t('message.SETTINGS_SAML')"
          class="config-details__settings__sso"
          use-transition-expand
        >
          <div>
            <!-- SSO SAML Activation -->
            <BetaTag :message="$t('message.BETA_SSO_LOGIN')" />
            <br>
            <Checkbox
              :value="Number(company.enableSaml) === 1"
              :title="$t('message.CONFIG_ENABLE_SAML')"
              @input="enableSamlOidc($event)"
            />
            <Checkbox
              :value="Number(company.forceSaml) === 1"
              :title="$t('message.CONFIG_FORCE_SAML')"
              class="block"
              @input="forceSamlOidc($event)"
            />
            <div class="field-hint">{{ $t('message.CONFIG_FORCE_SSO_HINT') }}</div>
            <div class="sep" />
            <!-- SSO SAML IdP -->
            <h3>{{ $t('message.CONFIG_COMPANY_SSO_IDP') }}</h3>
            <EditableLabel
              v-model="company.samlEntityId"
              :legend="`${$t('message.CONFIG_SAML_ENTITY_ID')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SAML_ENTITY_ID')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <EditableLabel
              v-model="company.samlSsoUrl"
              :legend="`${$t('message.CONFIG_SAML_SSO_URL')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SAML_SSO_URL')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <EditableLabel
              v-model="company.samlSloUrl"
              :legend="`${$t('message.CONFIG_SAML_SLO_URL')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SAML_SLO_URL')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <EditableLabel
              v-model="company.samlx509cert"
              :legend="`${$t('message.CONFIG_SAML_CERT')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SAML_CERT')"
              class="clickable-hoverable"
              use-textarea
              @input="setParams"
            />
            <IntCheckbox
              v-model="company.samlRequestedAuthnContext"
              :title="$t('message.CONFIG_SAML_PASSWORD_PROTECTED')"
              @input="setParams"
            />
            <div class="v-centerer config-details__settings__sso__import">
              <button class="action-btn fine" @click="$refs.samlFile.click()">
                <span for="saml-file" class="btn" style="cursor: pointer;">
                  {{ $t('message.CONFIG_IMPORT_SAML_XML_META') }}
                </span>
                <input
                  ref="samlFile"
                  type="file"
                  style="visibility: hidden; width: 0px; height: 0px;"
                  @change="samlInputFile"
                >
              </button>
            </div>
            <div class="field-hint">
              {{ $t('message.CONFIG_IMPORT_SAML_META_HINT') }}
            </div>
            <!-- SSO SAML SP -->
            <div class="sep" />
            <h3> {{ $t('message.CONFIG_SHIZEN_SSO_SP') }}</h3>
            <EditableLabel
              :value="samlSPEntity"
              :legend="`${$t('message.CONFIG_SAML_SP_ENTITY')}${$t('message.TWO_DOTS')}`"
              disabled
            />
            <EditableLabel
              :value="samlAcsUrl"
              :legend="`${$t('message.CONFIG_SAML_ACS_URL')}${$t('message.TWO_DOTS')}`"
              disabled
            />
            <EditableLabel
              :value="samlSlsUrl"
              :legend="`${$t('message.CONFIG_SAML_SLS_URL')}${$t('message.TWO_DOTS')}`"
              disabled
            />
            <a
              class="action-btn fine"
              :class="{ 'greyed-out': exportMetadata === null }"
              download="shizen-sp-metadata.xml"
              :href="exportMetadata"
            >
              {{ $t('message.CONFIG_EXPORT_SAML_XML_META') }}
            </a>
            <!-- SSO SAML Mapper -->
            <div class="sep" />
            <h3>{{ $t('message.CONFIG_SSO_MATCH_IDP_SHIZEN') }}</h3>
            <div class="field-hint">{{ $t('message.CONFIG_SSO_MATCH_IDP_SHIZEN_HINT') }}</div>
            <EditableLabel
              v-model="company.samlMapperIdPProperty"
              :legend="`${$t('message.CONFIG_SSO_MAPPER_IDP_PROP')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SSO_MAPPER_IDP_PROP')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <EditableLabelRadio
              v-model="company.samlMapperSPProperty"
              :legend="`${$t('message.CONFIG_SSO_MAPPER_SHIZEN_PROP')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SSO_MAPPER_SHIZEN_PROP')"
              :items="samlMapperSPItems"
              class="clickable-hoverable"
              @input="setParams"
            />
            <div class="field-hint">{{ samlMapperExampleHint }}</div>
            <div class="sep" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="$t('message.CONFIG_SSO_AUTO_CREATE_USERS_HINT')" />
            <div>
              <div
                v-for="attr in ssoRequiredAttributes"
                :key="attr.tr"
                class="config-details__settings__sso__mapper-item"
              >
                <div class="legend">{{ `${$t(attr.tr)}${$t('message.TWO_DOTS')}` }}</div>
                <div class="value">
                  <EditableLabel
                    :value="company.samlUserMapper[attr.prop] || ''"
                    :placeholder="attr.placeholder"
                    use-textarea
                    @input="userMapper(company.samlUserMapper, attr, $event)"
                  />
                </div>
              </div>
            </div>
            <br>
            <a
              class="action-btn fine"
              target="_blank"
              :class="{ 'greyed-out': !Number(company.enableSaml) }"
              :href="Number(company.enableSaml) ? '/server/saml/test' : ''"
            >
              {{ $t('message.CONFIG_TEST_SSO_LOGIN') }}
            </a>
            <!-- <IntCheckbox
              v-model="company.samlMapperUseFriendlyName"
              :title="$t('message.CONFIG_SAML_MAPPER_FRIENDLY_NAME')"
              @input="setParams"
            /> -->
          </div>
        </ExpandableSettingsItem>

        <!-- 3rd item : SSO OIDC -->
        <ExpandableSettingsItem
          :title="$t('message.CONFIG_SETTINGS_OIDC')"
          class="config-details__settings__sso"
          use-transition-expand
        >
          <div>
            <!-- SSO OIDC Activation -->
            <BetaTag :message="$t('message.BETA_SSO_LOGIN')" />
            <br>
            <Checkbox
              :value="Number(company.enableOidc) === 1"
              :title="$t('message.CONFIG_ENABLE_OIDC')"
              @input="enableSamlOidc($event, false, true)"
            />
            <Checkbox
              :value="Number(company.forceOidc) === 1"
              :title="$t('message.CONFIG_FORCE_OIDC')"
              class="block"
              @input="forceSamlOidc($event, true)"
            />
            <div class="field-hint">{{ $t('message.CONFIG_FORCE_SSO_HINT') }}</div>
            <!-- SSO OIDC IdP -->
            <div class="sep" />
            <h3>{{ $t('message.CONFIG_COMPANY_SSO_IDP') }}</h3>
            <EditableLabel
              v-model="company.oidcClientId"
              :legend="`${$t('message.CONFIG_OIDC_CLIENT_ID')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_OIDC_CLIENT_ID')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <EditableLabel
              v-model="company.oidcServerUrl"
              :legend="`${$t('message.CONFIG_OIDC_SERVER_URL')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_OIDC_SERVER_URL')"
              class="clickable-hoverable config-details__settings__sso__oidc-url"
              @input="setParams"
            />
            <div class="field-hint config-details__settings__sso__oidc-url-hint">
              <div class="v-centerer">
                <div class="config-details__settings__sso__oidc-url-hint__url">
                  {{ `https://&lt;${$t('message.CONFIG_OIDC_SERVER_URL')}&gt;` }}
                </div>
                <div class="config-details__settings__sso__oidc-url-hint__suffix">
                  {{ '/.well-known/openid-configuration' }}
                </div>
              </div>
            </div>
            <EditableLabel
              v-model="company.oidcSecret"
              :legend="`${$t('message.CONFIG_OIDC_SECRET')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_OIDC_SECRET')"
              class="clickable-hoverable"
              use-textarea
              @input="setParams"
            />
            <!-- SSO OIDC SP -->
            <div class="sep" />
            <h3> {{ $t('message.CONFIG_SHIZEN_SSO_SP') }}</h3>
            <EditableLabel
              :value="oidcAcsUrl"
              :legend="`${$t('message.CONFIG_OIDC_ACS_URL')}${$t('message.TWO_DOTS')}`"
              disabled
            />
            <EditableLabel
              :value="oidcSloUrl"
              :legend="`${$t('message.CONFIG_OIDC_SLO_URL')}${$t('message.TWO_DOTS')}`"
              disabled
            />
            <!-- SSO OIDC Mapper -->
            <div class="sep" />
            <h3>{{ $t('message.CONFIG_SSO_MATCH_IDP_SHIZEN') }}</h3>
            <div class="field-hint">
              {{ $t('message.CONFIG_SSO_MATCH_IDP_SHIZEN_HINT') }}
            </div>
            <EditableLabel
              v-model="company.oidcMapperIdPProperty"
              :legend="`${$t('message.CONFIG_SSO_MAPPER_IDP_PROP')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SSO_MAPPER_IDP_PROP')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <EditableLabelRadio
              v-model="company.oidcMapperSPProperty"
              :legend="`${$t('message.CONFIG_SSO_MAPPER_SHIZEN_PROP')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_SSO_MAPPER_SHIZEN_PROP')"
              :items="samlMapperSPItems"
              class="clickable-hoverable"
              @input="setParams"
            />
            <div class="sep" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="field-hint" v-html="$t('message.CONFIG_OIDC_SCOPES_HINT')" />
            <EditableLabel
              v-model="company.oidcScopes"
              :legend="`${$t('message.CONFIG_OIDC_SCOPES')}${$t('message.TWO_DOTS')}`"
              :placeholder="$t('message.CONFIG_OIDC_SCOPES_PH')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="$t('message.CONFIG_SSO_AUTO_CREATE_USERS_HINT')" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="field-hint" v-html="$t('message.CONFIG_SSO_OIDC_ADD_CLAIM_HINT')" />
            <div>
              <div
                v-for="attr in ssoRequiredAttributes"
                :key="attr.tr"
                class="config-details__settings__sso__mapper-item"
              >
                <div class="legend">{{ `${$t(attr.tr)}${$t('message.TWO_DOTS')}` }}</div>
                <div class="value">
                  <EditableLabel
                    :value="company.oidcUserMapper[attr.prop] || ''"
                    :placeholder="attr.placeholder"
                    use-textarea
                    @input="userMapper(company.oidcUserMapper, attr, $event)"
                  />
                </div>
              </div>
            </div>
            <br>
            <a
              class="action-btn"
              target="_blank"
              :class="{ 'greyed-out': Number(company.enableOidc) === 0 }"
              href="/server/oidc/test"
            >
              {{ $t('message.CONFIG_TEST_SSO_LOGIN') }}
            </a>
          </div>
        </ExpandableSettingsItem>

        <!-- 4th item : Indicators settings -->
        <ExpandableSettingsItem :title="$t('message.INDICATORS')" use-transition-expand>
          <div>
            <span class="legend">{{ $t("message.CFG_CHARTS_COLOR") + $t("message.TWO_DOTS") }}</span>
            <RadioGroup v-model="company.chartColor" :items="chartColorsChoices" @input="setParams" />
            <div class="legend mt10">
              {{ $t("message.CFG_CHARTS_FILLING") + $t("message.TWO_DOTS") }}
            </div>
            <IntCheckbox
              v-model="company.fillGoodValues"
              :title="$t('message.CFG_CHARTS_FILL_GOOD')"
              @input="setParams"
            />
            <br>
            <IntCheckbox
              v-model="company.fillBadValues"
              :title="$t('message.CFG_CHARTS_FILL_BAD')"
              @input="setParams"
            />
            <EditableLabel
              v-model="company.chartColorAlpha"
              :legend="$t('message.CFG_CHARTS_COLOR_OPACITY') + $t('message.TWO_DOTS')"
              class="clickable-hoverable"
              @input="setParams"
            />
            <div class="field-hint">
              {{ $t('message.CFG_CHARTS_COLOR_OPACITY_HINT') }}
            </div>
          </div>
        </ExpandableSettingsItem>
      </div>
    </div>
  </div>
</template>

<script>
import toastr from 'toastr';
import { mapState } from 'vuex';
// Components
import BetaTag from '@/components/beta-tag.vue';
import Checkbox from '@/components/checkbox.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import PwdEditForm from '@/components/pwd-edit-form.vue';
import RadioGroup from '@/components/radio-group.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Mixins
import mixinConfigPages from '@/mixins/config-pages.vue';
// Vuex utils
import { FETCH_TEAMS } from '@/store/action-types';
import { GET_LOGO, GET_TEAMS } from '@/store/mutation-types';
import { SET_COMPANY_PARAM, FETCH_CONFIG_USERS } from '@/store/modules/config/action-types';
import { GET_CONFIG_USERS } from '@/store/modules/config/mutation-types';
// Utils
import { calert, preventPastingStyles } from '@/utils/main';
import { setAsyncValue } from '@/utils/async';
import { parseSamlIDPXml, samlAttributeNames, samlNecesaryAttributes } from '@/utils/sso-utils';

export default {

  name: 'ConfigDetails',

  components: {
    BetaTag,
    Checkbox,
    EditableLabel,
    EditableLabelRadio,
    ExpandableSettingsItem,
    IntCheckbox,
    PwdEditForm,
    RadioGroup,
    TransitionExpand,
  },

  mixins: [mixinConfigPages],

  data() {
    return {
      editingPassword: false,
      chartColorsChoices: [
        { id: 0, label: this.$t('message.CFG_COLOR_BLUE') },
        { id: 1, label: this.$t('message.CFG_COLOR_GREEN') },
      ],
      allEditableParams: [
        'chartColor',
        'chartColorAlpha',
        'fillGoodValues',
        'fillBadValues',
        'enableSaml',
        'forceSaml',
        'samlEntityId',
        'samlSsoUrl',
        'samlSloUrl',
        'samlx509cert',
        'samlMapperSPProperty',
        'samlMapperIdPProperty',
        'samlUserMapper',
        'samlRequestedAuthnContext',
        'enableOidc',
        'forceOidc',
        'oidcClientId',
        'oidcServerUrl',
        'oidcSecret',
        'oidcMapperIdPProperty',
        'oidcMapperSPProperty',
        'oidcUserMapper',
        'oidcScopes',
        'allowSendingInfoAnyTeam',
        'forceActionPostponementReason',
        'disallowGWCompletedEditing',
        'restrictSettingAccessUser',
        'restrictProblemCreationUpdate',
      ],
      rootUrl: process.env.VUE_APP_URL,
      exportMetadata: null,
      samlMapperSPItems: [
        { id: 'email', label: this.$t('message.EMAIL') },
        { id: 'username', label: this.$t('message.USERNAME') },
        { id: 'ssoId', label: this.$t('message.CONFIG_USER_SSO_ID') },
      ],
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.COMPANY_DETAILS'),
    };
  },

  computed: {
    /** String: Entity ID for SAML protocol */
    samlSPEntity() {
      return `${this.rootUrl}/server/saml/metadata`;
    },
    /** String: Assertion consumer service URL for SAML protocol */
    samlAcsUrl() {
      return `${this.rootUrl}/server/saml/acs`;
    },
    /** String: Logout Service URL for SAML protocol */
    samlSlsUrl() {
      return `${this.rootUrl}/server/saml/sls`;
    },
    /** String: OIDC redirect URL after login */
    oidcAcsUrl() {
      return `${this.rootUrl}/server/oidc/acs`;
    },
    /** String: OIDC logout URL */
    oidcSloUrl() {
      return `${this.rootUrl}/server/oidc/slo`;
    },
    /** String: Hint to explain how the mapper works with the current values */
    samlMapperExampleHint() {
      const shizenProperty = this.$t(`message.CONFIG_SAML_SP_PROPS.${this.company.samlMapperSPProperty}`);
      let idpProperty = samlAttributeNames[this.company.samlMapperIdPProperty];
      if (idpProperty === undefined) {
        idpProperty = Object.values(samlAttributeNames)
          .find(a => a.uri === this.company.samlMapperIdPProperty);
        if (idpProperty === undefined) {
          return '';
        }
      }
      idpProperty = idpProperty.friendlyName;
      return this.$t('message.CONFIG_SAML_EXAMPLE_HINT', { shizenProperty, idpProperty });
    },
    /** Array: Required attributes in order to automatically create a user if no match found on SSO */
    ssoRequiredAttributes() {
      const attrs = [
        { prop: 'firstName', samlProp: 'givenName', tr: 'message.FIRST_NAME' },
        { prop: 'lastName', samlProp: 'surname', tr: 'message.LAST_NAME' },
        { prop: 'email', samlProp: 'email', tr: 'message.EMAIL' },
        { prop: 'groups', samlProp: 'groups', tr: 'message.CONFIG_SSO_IDP_GROUPS' },
      ];
      const exampleStr = `${this.$t('message.EXAMPLE')}${this.$t('message.TWO_DOTS')}`;
      attrs.forEach((attr) => {
        const phs = [];
        if (samlNecesaryAttributes[attr.samlProp]) {
          const { uri, shibbolethUri, oidcStandardClaim } = samlNecesaryAttributes[attr.samlProp];
          if (oidcStandardClaim && oidcStandardClaim.length > 0) {
            phs.push(oidcStandardClaim);
          }
          if (uri && uri.length > 0) {
            phs.push(uri);
          }
          if (shibbolethUri && shibbolethUri.length > 0) {
            phs.push(shibbolethUri);
          }
        }
        attr.placeholder = `${exampleStr}${phs.join(', ')}`;
      });
      return attrs;
    },
    ...mapState({
      stateTeams: state => state[GET_TEAMS.stateKey] || [],
      stateTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
    ...mapState('config', {
      stateUsers: state => state[GET_CONFIG_USERS.stateKey] || [],
      stateUsersLoading: state => state[GET_CONFIG_USERS.loadingKey],
    }),
  },

  mounted() {
    this.$nextTick(preventPastingStyles);
  },

  methods: {
    /** Updates the password and sends to server */
    updatePwd(passFormer, passNew, passNewConfirm) {
      const len = passNew.length;
      if (len < 6 || len > 25) {
        calert(this.$t('message.PWD_BETWEEN_8_25'), [{ title: this.$t('message.OK') }]);
        return;
      }
      if (passNewConfirm !== passNew) {
        calert(this.$t('message.PWD_DONT_MATCH'), [{ title: this.$t('message.OK') }]);
        return;
      }
      // Send to server
      this.App.post('/server/config/editPwd.php', { former: passFormer, new: passNew }, (d) => {
        if (d === 1) {
          toastr.success(this.$t('message.PWD_EDITED'));
          this.editingPassword = false;
        } else {
          toastr.error(this.$t('message.PWD_ERROR_CHECK'));
        }
      });
    },
    /** Update the logo and whiteBg parameters */
    updateLogo() {
      const { logo, logoWhiteBg } = this.company;
      const newLogo = { url: logo, whiteBg: Number(logoWhiteBg) === 1 };
      this.App.post('/server/config/setLogo.php', newLogo, (d) => {
        if (d === 1) {
          setAsyncValue(this.$store, GET_LOGO, newLogo);
          toastr.success(this.$t('message.LOGO_UPDATED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Set company params */
    setParams() {
      const params = {};
      this.allEditableParams.forEach((param) => {
        params[param] = this.company[param];
      });
      this.$store.dispatch(`config/${SET_COMPANY_PARAM}`, params);
    },
    /**
     * Enable/Disable SAML or OIDC. Disabling it should also disable the 'force' param
     * @param  {Boolean}  doEnable          - Value of enableSaml or enableOidc
     * @param  {Boolean} [doForce=false]    - Whether the force value should be set to true or remain
     * @param  {Boolean} [forOidc=false]    - Whether we are updating enableOidc (true) or enableSaml (false)
     */
    enableSamlOidc(doEnable, doForce = false, forOidc = false) {
      let [enableKey, forceKey, opEnable, opForce] = ['enableSaml', 'forceSaml', 'enableOidc', 'forceOidc'];
      if (forOidc) {
        [enableKey, forceKey, opEnable, opForce] = [opEnable, opForce, enableKey, forceKey];
      }
      if (doEnable && Number(this.company[opEnable])) {
        calert(this.$t('message.CONFIG_CANT_SSO_BOTH_SAML_OIDC'));
        return;
      }
      if (!doEnable) {
        this.company[forceKey] = 0;
      } else if (doForce) {
        this.company[forceKey] = 1;
      }
      this.company[enableKey] = doEnable;
      this.setParams();
    },
    /**
     * Force/Unforce SAML or OIDC.
     * Forcing one or the other automatically checks the 'enable' param (enableSaml or enableOidc)
     * @param  {Boolean}  doForce         - Value of forceSaml or forceOidc
     * @param  {Boolean} [forOidc=false]  - Whether we are updating forceOidc or forceSaml
     */
    forceSamlOidc(doForce, forOidc = false) {
      let [enableKey, forceKey, opEnable, opForce] = ['enableSaml', 'forceSaml', 'enableOidc', 'forceOidc'];
      if (forOidc) {
        [enableKey, forceKey, opEnable, opForce] = [opEnable, opForce, enableKey, forceKey];
      }
      if (doForce && !Number(this.company[enableKey])) {
        this.enableSamlOidc(true, true, forOidc);
        return;
      }
      this.company[forceKey] = doForce;
      this.setParams();
    },
    /** Parse and save metadata file */
    samlInputFile(evt) {
      const file = evt.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const data = parseSamlIDPXml(reader.result);
        if (!data) {
          toastr.error(this.$t('message.CONFIG_SAML_META_FILE_WRONG'));
        } else {
          this.company.samlEntityId = data.entityId;
          this.company.samlSsoUrl = data.ssoUrl;
          this.company.samlSloUrl = data.sloUrl;
          this.company.samlx509cert = data.x509CertString;
          this.setParams();
        }
      });
      reader.readAsText(file);
    },
    /**
     * Set a userMapper value and save
     * @param  {Object} mapper - samlUserdata or oidcUserMapper
     * @param  {Object} attr   - attr from this.ssoRequiredAttributes
     * @param  {String} val    - value to set
     */
    userMapper(mapper, attr, val) {
      mapper[attr.prop] = val;
      this.setParams();
    },
    refresh() {
      this.$http({
        method: 'get',
        url: '/server/saml/metadata.php',
      }).then((data) => {
        this.exportMetadata = window.URL.createObjectURL(new Blob([data.data], { type: 'text/plain' }));
      });

      if (!this.stateTeamsLoading) {
        this.$store.dispatch(FETCH_TEAMS, { config: 1 });
      }
      if (!this.stateUsersLoading) {
        this.$store.dispatch(`config/${FETCH_CONFIG_USERS}`);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

$content-padding: 1.5em;

.config-details {
  overflow: auto;
  padding: 0 $page-content-padding;

  &__infos {
    padding: $page-content-padding;

    .editable-label {
      margin-bottom: .5rem;
    }
  }

  .add-button img {
    width: 17px;
  }

  .pwd-edit-form {
    > div {
      margin: 5px 10px;
    }
    > button {
      margin-left: 10px;
    }
  }


  &__settings {
    margin: 2em 0;

    h2 {
      margin: 0px 2em;
    }

    > .settings-dropdown {
      margin: 0 -#{$content-padding};
    }

    &__sso {
      user-select: text;

      * {
        user-select: text;
      }
      .sep {
        margin: 0.8em 0em;
        width: 100%;
      }
      h3 {
        margin-bottom: 0.4em;
      }
      .editable-label {
        padding: 0.3em 0px;
      }
      &__import {
        margin: 0.6em 0px 0.3em 0px;
      }
      &__mapper-item {
        display: flex;
        align-items: center;

        .legend {
          flex: 0 0 20%;
        }
        .value {
          flex: 1 0 80%;

          .editable-label {
            padding: 0;
          }
        }
      }

      & &__oidc-url {
        padding-bottom: 0.1em;
      }

      &__oidc-url-hint {
        padding-bottom: 0.3em;

        &__url {
          position: relative;

          &::after {
            content: " ";
            position: absolute;
            bottom: -2px;
            left: 0px;
            width: 100%;
            height: 3px;
            border: 2px solid $color-cyan;
            border-top: none;
          }
        }
        &__suffix {
          opacity: 0.8;
        }
      }
    }
  }
}
</style>
